import { Injectable, inject } from "@angular/core";
import { Notification } from "@app/shared/models/response.model";
import { I18nService } from "@app/core/services/i18n.service";
import { PortalSettingsService } from "@app/core/services/portal-settings.service";
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";

@Injectable({
  providedIn: "root"
})
export class NotifyService {
  private snackBar = inject(MatSnackBar);
  private i18n = inject(I18nService);
  private portalSettings = inject(PortalSettingsService);
  private _hideNotifications: boolean;

  /**
   * Pop a success message
   * @param title
   * @param message
   */
  success(title: string, message: string): MatSnackBarRef<SimpleSnackBar> {
    const options = new MatSnackBarConfig();
    options.panelClass = ["snackbar", "snackbar--success"];
    return this._showSnackBarMessage(title, message, options);
  }

  /**
   * Pop a warning message
   * @param title
   * @param message
   */
  warning(title: string, message: string): MatSnackBarRef<SimpleSnackBar> {
    const options = new MatSnackBarConfig();
    options.panelClass = ["snackbar", "snackbar--warning"];
    return this._showSnackBarMessage(title, message, options);
  }

  /**
   * Pop an error message
   * @param title
   * @param message
   */
  error(title: string, message: string, action?: string): MatSnackBarRef<SimpleSnackBar> {
    const options = new MatSnackBarConfig();
    options.panelClass = ["snackbar", "snackbar--error"];
    return this._showSnackBarMessage(title, message, options, action);
  }

  /**
   * Pop an informational message
   * @param title
   * @param message
   */
  info(title: string, message: string): MatSnackBarRef<SimpleSnackBar> {
    const options = new MatSnackBarConfig();
    options.panelClass = ["snackbar", "snackbar--info"];
    return this._showSnackBarMessage(title, message, options);
  }

  /**
   * Pops a reload message for PWA.
   * This snack bar overrides the default duration time and will show until user performs the reload action.
   */
  reload(title: string, message: string, action?: string): MatSnackBarRef<SimpleSnackBar> {
    const options = new MatSnackBarConfig();
    options.panelClass = ["snackbar", "snackbar--reload"];
    return this._showSnackBarMessage(title, message, options, action, 0);
  }

  /**
   * Pop an info message with printer icon
   * @param title
   * @param message
   */
  printer(title: string, message: string): MatSnackBarRef<SimpleSnackBar> {
    const options = new MatSnackBarConfig();
    options.panelClass = ["snackbar", "snackbar--printer"];
    return this._showSnackBarMessage(title, message, options, this.i18n.translate("APP.LBL_VIEW_REPORTS"));
  }

  /**
   * Reusable copy message
   * @param value
   */
  copy(value: string) {
    const title = this.i18n.translate("APP.LBL_COPIED", { value: value });
    this.success("", title);
  }

  /**
   * Clear any visible messages
   */
  clear() {
    this.snackBar.dismiss();
  }

  /**
   * Display a toaster message for every array element
   * @param notifications
   */
  popNotifications(notifications: Notification[]) {
    notifications.forEach((notification: Notification) => {
      switch (notification.type) {
        case "e":
          this.error(notification.title, notification.message);
          break;
        case "w":
          this.warning(notification.title, notification.message);
          break;
        case "i":
          this.info(notification.title, notification.message);
          break;
        default:
          this.success(notification.title, notification.message);
      }
    });
  }

  /**
   * applicationError()
   * @param message
   */
  applicationError(message: string): MatSnackBarRef<SimpleSnackBar> {
    const action = "DISMISS";
    const options = new MatSnackBarConfig();
    options.verticalPosition = "top";
    options.horizontalPosition = "center";
    options.duration = 0;
    options.panelClass = ["snackbar", "snackbar--error"];
    return this.snackBar.open(message, action, options);
  }

  openMessage(message: string, action?: string, options?: MatSnackBarConfig) {
    return this.snackBar.open(message, action, options);
  }

  /**
   * This will hide all notifications in the app. Used in scenarios where we are running services behind the scenes and dont want the user notified.
   */
  hideNotifications() {
    this._hideNotifications = true;
  }

  /**
   * Show notifications as usual
   */
  showNotifications() {
    this._hideNotifications = false;
  }

  get isHiding(): boolean {
    return this._hideNotifications;
  }

  get isOpen() {
    return this.snackBar._openedSnackBarRef !== null;
  }

  /**
   * Show a snackbar message
   * @param title
   * @param message
   * @param options
   *
   * NOTE: Titles were removed for snackbars.
   *       If we switch to toasters or alerts
   *       titles make more sense.
   */
  private _showSnackBarMessage(
    _title: string,
    message: string,
    options: MatSnackBarConfig,
    action?: string,
    duration?: number,
    messageData?: string
  ): MatSnackBarRef<SimpleSnackBar> {
    const opt = this.portalSettings.snackBarDefaultOptions;
    opt.duration = duration === 0 ? duration : opt.duration;
    opt.panelClass = options.panelClass;

    if (this._hideNotifications) {
      return undefined;
    }

    return this.snackBar.open(
      message
        ? this.i18n.translate(message, {
            data: messageData
          })
        : opt.announcementMessage,
      action ? action : this.i18n.translate("APP.LBL_DISMISS"),
      opt
    );
  }
}
